import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./attachmentPopUp.module.css";
import pdfIcon from "../../../assets/images/pdf.svg";
import viewIcon from "../../../assets/images/show.svg";
import deleteIcon from "../../../assets/images/Trash.svg";
import useFilesApi from "../../../api/filesApi";
import DocumentPreviewModal from "../preview/DocumentPreviewModal";

const extractUploadErrorMessage = (err) => {
  const status = err?.response?.status;
  const responseData = err?.response?.data;
  const rawMessageCandidates = [
    responseData?.message,
    responseData?.error,
    responseData?.detail,
    responseData?.title,
    typeof responseData === "string" ? responseData : "",
    err?.message,
  ];

  const apiMessage = rawMessageCandidates
    .map((value) => String(value || "").trim())
    .find(Boolean);
  const normalizedApiMessage = apiMessage
    ? apiMessage.replace(/\s+/g, " ").trim()
    : "";
  const lowerApiMessage = normalizedApiMessage.toLowerCase();

  if (
    status === 413 ||
    lowerApiMessage.includes("too large") ||
    lowerApiMessage.includes("request entity is too large")
  ) {
    return "Upload failed: file exceeds the server upload size limit (413).";
  }

  if (normalizedApiMessage && status) {
    return `Upload failed: ${normalizedApiMessage} (${status}).`;
  }

  if (normalizedApiMessage) {
    return `Upload failed: ${normalizedApiMessage}`;
  }

  if (status === 404) {
    return "Upload failed: Standard not found (404).";
  }

  if (status) {
    return `Upload failed (HTTP ${status}).`;
  }

  return "Upload failed. Please try again.";
};

const AttachmentPopup = ({ standardId, handleClose, onUploaded }) => {
  const {
    uploadDocumentFile,
    uploadReferenceFile,
    uploadBriefingMaterialFile,
  } = useFilesApi();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploadType, setUploadType] = useState("document");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef(null);

  const closePreview = useCallback(() => {
    setPreviewFile((current) => {
      if (current?.blobUrl) {
        window.URL.revokeObjectURL(current.blobUrl);
      }
      return null;
    });
  }, []);

  useEffect(() => closePreview, [closePreview]);

  const handleFile = (file) => {
    if (!file) return;
    closePreview();
    setSelectedFile(file);
  };

  const handleBrowse = () => {
    inputRef.current?.click();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const handleCreate = async () => {
    if (!standardId || !selectedFile || busy) return;
    setBusy(true);
    setErrorMsg("");
    try {
      if (uploadType === "briefing") {
        await uploadBriefingMaterialFile(standardId, selectedFile);
      } else if (uploadType === "reference") {
        await uploadReferenceFile(standardId, selectedFile);
      } else {
        await uploadDocumentFile(standardId, selectedFile);
      }
      closePreview();
      onUploaded?.();
      handleClose?.();
    } catch (err) {
      console.error(err);
      setErrorMsg(extractUploadErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = () => {
    if (!selectedFile) return;

    closePreview();
    const blobUrl = window.URL.createObjectURL(selectedFile);
    setPreviewFile({
      name: selectedFile.name,
      contentType: selectedFile.type,
      blobUrl,
    });
  };

  return (
    <>
      <div className={styles.overlay}>
        <div className={styles.modalBox}>
          <h3 className={styles.modalTitle}>Upload File</h3>

          <div
            className={styles.uploadArea}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <p className={styles.uploadText}>Drag & Drop file here</p>
            <p className={styles.uploadBrowse}>
              or <span onClick={!busy ? handleBrowse : undefined}>Browse</span>
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            onChange={(e) => handleFile(e.target.files?.[0])}
            style={{ display: "none" }}
          />

          {selectedFile && (
            <div className={styles.attachmentRow}>
              <div className={styles.attachmentInfo}>
                <img src={pdfIcon} className={styles.fileIcon} alt="File" />
                <div>
                  <p className={styles.fileName}>{selectedFile.name}</p>
                  <p className={styles.fileSize}>
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>

              <div className={styles.attachmentActions}>
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={busy}
                  title="Preview selected file"
                >
                  <img src={viewIcon} alt="Preview" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closePreview();
                    setSelectedFile(null);
                  }}
                  disabled={busy}
                >
                  <img src={deleteIcon} alt="Remove" />
                </button>
              </div>
            </div>
          )}

          <div className={styles.attachmentRow}>
            <label>
              <input
                type="radio"
                name="uploadType"
                value="document"
                checked={uploadType === "document"}
                onChange={() => setUploadType("document")}
                disabled={busy}
              />
              Document
            </label>
            <label>
              <input
                type="radio"
                name="uploadType"
                value="reference"
                checked={uploadType === "reference"}
                onChange={() => setUploadType("reference")}
                disabled={busy}
              />
              Reference
            </label>
            <label>
              <input
                type="radio"
                name="uploadType"
                value="briefing"
                checked={uploadType === "briefing"}
                onChange={() => setUploadType("briefing")}
                disabled={busy}
              />
              Briefing Material
            </label>
          </div>

          {errorMsg && <p className={styles.errorText}>{errorMsg}</p>}

          <div className={styles.modalActions}>
            <button className={styles.btnSecondary} onClick={handleClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={handleCreate}
              disabled={!selectedFile || busy}
            >
              {busy ? (
                <>
                  <span
                    className={styles.buttonSpinner}
                    role="status"
                    aria-label="Uploading file"
                  />
                  <span>Creating</span>
                </>
              ) : (
                "Create"
              )}
            </button>
          </div>
        </div>
      </div>

      <DocumentPreviewModal
        isOpen={Boolean(previewFile)}
        file={previewFile}
        onClose={closePreview}
      />
    </>
  );
};

export default AttachmentPopup;

